import {
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  formatISO,
  sub,
} from 'date-fns';

import { MAX_AGE_WEEKS, MIN_AGE_WEEKS } from '@farmersdog/constants';

export type BirthdayAccuracy = 'date' | 'weeks' | 'months' | 'years';

export const ValidationMessages = {
  minSignup: `Come back when your puppy is ${MIN_AGE_WEEKS} weeks old!`,
  minEdit: `Are you sure? That seems very young!`,
  max: `Are you sure? That seems quite old!`,
};

/**
 * Return a pet birthday string (YYYY-MM-DD) given age and accuracy. E.g.
 *
 * @example
 * convertAgeToBirthday(5, 'years', '2019-05-21') // `2014-05-21`.
 *
 * @param age - the number of the age
 * @param accuracy - The birthday accuracy ('years', 'months', or
 * 'weeks')
 * @param createdAt - (ISO Date) The date to calculate the birthday
 * from, e.g. when the pet has been created. Defaults to today.
 *
 * @returns The birthday date in YYYY-MM-DD format.
 */
export function convertAgeToBirthday(
  age: number,
  accuracy: BirthdayAccuracy,
  createdAt?: string
) {
  const date = createdAt ? new Date(createdAt) : new Date();
  const duration = accuracy === 'date' ? 'years' : accuracy;

  const birthday = formatISO(sub(date, { [duration]: age }), {
    representation: 'date',
  });

  return birthday;
}

/**
 * Guess the birthday accuracy from a birthday date.
 *
 * @param birthday - Date as string
 * @returns Either `years`, `weeks` or `months`.
 */
export function guessBirthdayAccuracy(birthday: string) {
  const TWELVE_WEEKS = 12;
  const TWO_YEARS_IN_WEEKS = 52 * 2;

  const ageInWeeks = differenceInWeeks(new Date(), new Date(birthday), {
    roundingMethod: 'round',
  });

  let birthdayAccuracy: BirthdayAccuracy = 'years';
  if (ageInWeeks) {
    if (ageInWeeks < TWELVE_WEEKS) {
      birthdayAccuracy = 'weeks';
    } else if (ageInWeeks < TWO_YEARS_IN_WEEKS) {
      birthdayAccuracy = 'months';
    }
  }

  return birthdayAccuracy;
}

/**
 * Return the age with accuracy from a birthday as `{ age, birthdayAccuracy }`.
 * where `age` is to the value the user type in the pet form.
 *
 * @param birthday - Date as string
 * @param birthdayAccuracy - the birthday accuracy, `years`, `weeks` or `months`
 * @returns an object containing the age and accuracy
 */
export function convertBirthdayToAge(
  birthday: string,
  birthdayAccuracy?: BirthdayAccuracy | null
) {
  const accuracy =
    birthdayAccuracy && birthdayAccuracy !== 'date'
      ? birthdayAccuracy
      : guessBirthdayAccuracy(birthday);

  const age =
    accuracy === 'weeks'
      ? differenceInWeeks(new Date(), new Date(birthday), {
          roundingMethod: 'floor',
        })
      : accuracy === 'months'
        ? differenceInMonths(new Date(), new Date(birthday))
        : differenceInYears(new Date(), new Date(birthday));

  return { age, birthdayAccuracy: accuracy };
}

/**
 * Determines if a given birthday results in a pet that is too young for The Farmer's Dog
 *
 * @param birthday - Date as string
 * @returns True if invalid, false if valid
 */
export function validateBirthdayMin(birthday: string) {
  const diffInWeeks = differenceInWeeks(new Date(), new Date(birthday));
  return diffInWeeks < MIN_AGE_WEEKS;
}

/**
 * Determines if a given birthday results in a pet that is too old for The Farmer's Dog
 *
 * @param birthday - Date as string
 * @returns True if invalid, false if valid
 */
export function validateBirthdayMax(birthday: string) {
  const diffInWeeks = differenceInWeeks(new Date(), new Date(birthday));
  return diffInWeeks > MAX_AGE_WEEKS;
}

/**
 * Return a human-readable birthday in age, e.g.:
 *
 *  5 year old
 *
 * @param birthday - the birthday to format
 * @param options - Optional parameters
 *        options.plural - Leaves the s at the end of the accuracy, 5 years old
 */
export function formatBirthday(
  birthday: string | undefined | null,
  {
    plural = false,
    shouldHyphenate = false,
    accuracy = null,
  }: {
    plural?: boolean;
    shouldHyphenate?: boolean;
    accuracy?: BirthdayAccuracy | null;
  } = {}
) {
  if (!birthday) {
    return '';
  }

  const spacingCharacter = shouldHyphenate ? '-' : ' ';

  const { age, birthdayAccuracy } = convertBirthdayToAge(birthday, accuracy);
  const formattedAccuracy =
    !plural || age === 1
      ? birthdayAccuracy.replace(/s$/, '')
      : birthdayAccuracy;

  return `${age}${spacingCharacter}${formattedAccuracy}${spacingCharacter}old`;
}
